/**
 * PKCE (Proof Key for Code Exchange) Utilities
 * Used for secure SSO authentication flow
 */

/**
 * Generate a cryptographically random string
 * @param {number} length - Length of the string
 * @returns {string} Random string
 */
export function generateRandomString(length) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }

  return result;
}

/**
 * Calculate SHA256 hash of a string
 * @param {string} plain - Plain text to hash
 * @returns {Promise<string>} Hex encoded hash
 */
export async function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Generate PKCE pair (verifier + challenge)
 * @returns {Promise<{verifier: string, challenge: string}>}
 */
export async function generatePKCEPair() {
  const verifier = generateRandomString(64);
  const challenge = await sha256(verifier);

  return {
    verifier,
    challenge
  };
}

/**
 * Store PKCE verifier in sessionStorage
 * @param {string} verifier - The code verifier to store
 */
export function storePKCEVerifier(verifier) {
  sessionStorage.setItem('pkce_verifier', verifier);
}

/**
 * Retrieve PKCE verifier from sessionStorage
 * @returns {string|null} The stored verifier or null
 */
export function retrievePKCEVerifier() {
  return sessionStorage.getItem('pkce_verifier');
}

/**
 * Clear PKCE verifier from sessionStorage
 */
export function clearPKCEVerifier() {
  sessionStorage.removeItem('pkce_verifier');
}
